import React, { useEffect, useMemo, useState } from 'react'

import { LoginScreen } from '../pages/LoginScreen'
import { SignupScreen } from '../pages/SignupScreen'
import { ForgotPasswordScreen } from '../pages/ForgotPasswordScreen'
import { ResetPasswordScreen } from '../pages/ResetPasswordScreen'

type AuthModalMode = 'login' | 'signup' | 'forgot' | 'reset'

interface AuthModalShellProps {
  mode?: AuthModalMode | null
  onClose?: () => void
  onSwitch?: (mode: AuthModalMode) => void
  children?: React.ReactNode
}

export const AuthModalShell: React.FC<AuthModalShellProps> = ({ mode, onClose, onSwitch, children }) => {
  const isFlowOpen = mode === 'login' || mode === 'signup' || mode === 'forgot' || mode === 'reset'
  const [isMounted, setIsMounted] = useState(isFlowOpen)
  const [isVisible, setIsVisible] = useState(isFlowOpen)

  const activeMode = useMemo<AuthModalMode>(() => {
    if (mode === 'signup') return 'signup'
    if (mode === 'forgot') return 'forgot'
    if (mode === 'reset') return 'reset'
    return 'login'
  }, [mode])

  useEffect(() => {
    if (isFlowOpen) {
      setIsMounted(true)
      const raf = window.requestAnimationFrame(() => setIsVisible(true))
      return () => window.cancelAnimationFrame(raf)
    }
    if (isMounted) {
      setIsVisible(false)
      const timer = window.setTimeout(() => setIsMounted(false), 220)
      return () => window.clearTimeout(timer)
    }
  }, [isMounted, isFlowOpen])

  useEffect(() => {
    if (!isMounted) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose?.()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isMounted, onClose])

  const handleSwitch = (next: AuthModalMode) => {
    onSwitch?.(next)
  }

  return (
    <>
      {children}
      {isMounted && (
        <div
          data-auth-modal="true"
          className="fixed inset-0 z-[120] flex items-center justify-center px-4 py-10"
        >
          <button
            onClick={() => onClose?.()}
            className={`absolute inset-0 transition-all duration-200 ${
              isVisible ? 'bg-slate-900/60 backdrop-blur-sm opacity-100' : 'bg-slate-900/0 opacity-0'
            }`}
          />
          <div
            className={`relative w-full max-w-[520px] transition-all duration-200 ${
              isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-2'
            }`}
          >
            {activeMode === 'login' && (
              <LoginScreen
                variant="modal"
                onClose={onClose}
                onSwitch={() => handleSwitch('signup')}
              />
            )}
            {activeMode === 'signup' && (
              <SignupScreen
                variant="modal"
                onClose={onClose}
                onSwitch={() => handleSwitch('login')}
              />
            )}
            {activeMode === 'forgot' && (
              <ForgotPasswordScreen
                variant="modal"
                onClose={onClose}
                onBack={() => handleSwitch('login')}
                onNext={() => handleSwitch('reset')}
              />
            )}
            {activeMode === 'reset' && (
              <ResetPasswordScreen
                variant="modal"
                onClose={onClose}
                onBack={() => handleSwitch('login')}
                onComplete={() => handleSwitch('login')}
              />
            )}
          </div>
        </div>
      )}
    </>
  )
}
